import * as React from "react";
import { DomainDryRunDNSDetails, DomainInput } from "./types/input";
import NGAlert from "../../../newcomponents/alert";
import { DomainDryRunConfig } from "./domainDryRunConfig";
import { useFormContext } from "react-hook-form";
import { NGButton } from "../../../newcomponents/button/Button";
import { v4 as uuidv4 } from "uuid";

interface Props {
  dryRunPath: "raw" | "nameOnly" | "apex";
  showValid: boolean;
  onCreateApex?: any;
}
export const DomainDryRun: React.FC<Props> = ({ dryRunPath, showValid, onCreateApex }) => {
  const { watch, setValue } = useFormContext<DomainInput>();

  const dryRun = watch(`dryRun.${dryRunPath}.value`) || null;

  function onRetry() {
    setValue("dryRun.triggerer", uuidv4());
  }

  const mode = watch("mode") || "basic";
  const isLoadingRaw = watch("dryRun.raw.isLoading");
  const isLoadingNameOnly = watch("dryRun.nameOnly.isLoading");
  const isLoadingApex = watch("dryRun.apex.isLoading");
  const isLoading = React.useMemo(() => {
    if (dryRunPath === "nameOnly") return isLoadingNameOnly;
    if (dryRunPath === "apex") return isLoadingApex;
    return isLoadingRaw;
  }, [dryRunPath, isLoadingRaw, isLoadingNameOnly, isLoadingApex]);

  if (
    !dryRun ||
    (dryRun.status === 200 &&
      ((dryRun.details as DomainDryRunDNSDetails).dnsConfig || []).length < 1 &&
      ((dryRun.details as DomainDryRunDNSDetails).ownershipConfig || []).length < 1)
  ) {
    if (showValid) {
      return <NGAlert className="my-4" title="Domain Configuration is Valid" />;
    } else {
      return null;
    }
  }

  if (!!(dryRun.details as any)?.issues) {
    return null;
  }

  if (
    !dryRun.message &&
    !(dryRun.details as DomainDryRunDNSDetails)?.ownershipConfig &&
    !(dryRun.details as DomainDryRunDNSDetails)?.dnsConfig
  ) {
    return null;
  }

  return (
    <>
      <div className="border-2 rounded p-4 color-default">
        <div className="font-semibold">Domain Validity Check</div>
        {dryRun.message ? (
          <NGAlert
            type={dryRun.message === "Domain is OK" ? "info" : "error"}
            title={dryRun.message === "Domain is OK" ? "" : dryRunCodeToLabel(dryRun.code || dryRun.message)}
            message={
              dryRun.message === "Domain is OK"
                ? `Domain spec is valid.${dryRun.status === 200 ? ` You need to complete the DNS configuration.` : ""}`
                : dryRun.message
            }
            className="mt-4"
            size={"small"}
          />
        ) : null}
        <DomainDryRunConfig
          label={`Ownership Config`}
          configs={(dryRun.details as DomainDryRunDNSDetails)?.ownershipConfig}
        />
        <DomainDryRunConfig label={`DNS Config`} configs={(dryRun.details as DomainDryRunDNSDetails)?.dnsConfig} />
        {onCreateApex ? (
          <NGButton className="mt-4" variant="primary" size="small" onClick={onCreateApex}>
            Create Apex
          </NGButton>
        ) : null}
        {dryRun.status !== 200 &&
        ((dryRun.details as DomainDryRunDNSDetails)?.ownershipConfig ||
          (dryRun.details as DomainDryRunDNSDetails)?.dnsConfig) ? (
          <NGButton variant="primary" onClick={onRetry} loading={isLoading} disabled={isLoading}>
            I have configured DNS records, continue
          </NGButton>
        ) : null}
      </div>
    </>
  );
};

function dryRunCodeToLabel(code: string) {
  if (code === "apex_must_exist") {
    return "Apex Must Exist";
  }
  if (code === "must_prove_ownership") {
    return "Must Prove Ownership";
  }
  return code;
}
